import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import {
	Card,
	CardContent,
	CardDescription,
	CardFooter,
	CardHeader,
	CardTitle,
} from "@/components/ui/card"
import { usePlatform } from "@/platform/context"
import { mutators, queries } from "@oblique/shared/zero"
import { useQuery, useZero } from "@rocicorp/zero/react"
import { Check, Trash2, X } from "lucide-react"
import { useMemo, useState, type MouseEvent } from "react"
import { useNavigate } from "react-router"
import { toast } from "sonner"
import { v7 } from "uuid"
import { useAuth } from "../providers/auth"

export const Dashboard = () => {
	const { user } = useAuth()
	const navigate = useNavigate()
	const platform = usePlatform()
	const z = useZero()
	const [isCreatingProject, setIsCreatingProject] = useState(false)
	const [deletingProjectId, setDeletingProjectId] = useState<string | null>(
		null,
	)

	// Get all project memberships for the current user
	const [memberships, membershipsStatus] = useQuery(
		queries.members.byUser(user?.id ?? ""),
	)

	// Get pending invitations for the current user's email
	const [invitations, invitationsStatus] = useQuery(
		queries.invitations.pendingByEmail(user?.email ?? ""),
	)

	// Separate owned projects from shared projects
	const { ownedProjects, sharedProjects } = useMemo(() => {
		const owned: typeof memberships = []
		const shared: typeof memberships = []

		for (const membership of memberships) {
			if (membership.role === "owner") {
				owned.push(membership)
			} else {
				shared.push(membership)
			}
		}

		// Sort by updated_at descending
		const sortByUpdated = (
			a: (typeof memberships)[0],
			b: (typeof memberships)[0],
		) => {
			const aUpdated = a.project?.updated_at
				? new Date(a.project.updated_at).getTime()
				: 0
			const bUpdated = b.project?.updated_at
				? new Date(b.project.updated_at).getTime()
				: 0
			return bUpdated - aUpdated
		}

		return {
			ownedProjects: owned.sort(sortByUpdated),
			sharedProjects: shared.sort(sortByUpdated),
		}
	}, [memberships])

	const navigateToProject = (projectID: string, projectTitle: string) => {
		navigate(`/projects/${projectID}`, {
			state: {
				projectTitle,
			},
		})
	}

	const handleCreateProject = async () => {
		if (isCreatingProject) return

		const projectID = v7()
		const projectTitle = "New Project"
		const now = Date.now()
		setIsCreatingProject(true)

		try {
			const result = await z.mutate(
				mutators.projects.create({
					id: projectID,
					name: projectTitle,
					description: projectTitle,
					created_at: now,
					updated_at: now,
				}),
			).client

			if (result.type === "error") throw new Error(result.error.message)
			navigateToProject(projectID, projectTitle)
		} catch (error) {
			toast.error(
				error instanceof Error ? error.message : "Unable to create project",
			)
		} finally {
			setIsCreatingProject(false)
		}
	}

	const handleAcceptInvitation = (invitationId: string) => {
		z.mutate(
			mutators.invitations.accept({
				invitation_id: invitationId,
				member_id: v7(),
			}),
		)
	}

	const handleDeclineInvitation = (invitationId: string) => {
		z.mutate(
			mutators.invitations.decline({
				invitation_id: invitationId,
			}),
		)
	}

	const handleDeleteProject = async (
		event: MouseEvent<HTMLButtonElement>,
		projectId: string,
		projectName: string,
	) => {
		event.stopPropagation()
		if (
			!window.confirm(
				`Delete “${projectName}”? This permanently deletes the project and cannot be undone.`,
			)
		) {
			return
		}

		setDeletingProjectId(projectId)
		try {
			await z.mutate(mutators.projects.delete({ project_id: projectId })).server
			try {
				await platform.deleteProjectData?.(projectId)
			} catch (error) {
				toast.warning(
					error instanceof Error
						? `Project deleted, but local data cleanup failed: ${error.message}`
						: "Project deleted, but local data cleanup failed",
				)
				return
			}
			toast.success(`Deleted “${projectName}”`)
		} catch (error) {
			toast.error(
				error instanceof Error ? error.message : "Unable to delete project",
			)
		} finally {
			setDeletingProjectId(null)
		}
	}

	const isLoading =
		membershipsStatus.type === "unknown" || invitationsStatus.type === "unknown"

	return (
		<div className="origin-dashboard mx-auto flex w-full max-w-6xl flex-col gap-6 p-6">
			<section className="origin-dashboard-hero flex flex-col gap-4 rounded-xl border bg-card p-6 shadow-sm md:flex-row md:items-center md:justify-between">
				<div>
					<h2 className="text-xl font-semibold">Welcome back</h2>
					<p className="text-muted-foreground">
						{user?.name
							? `Ready to build, ${user.name}?`
							: "Your geometry workspace is synced and ready."}
					</p>
				</div>
				<Button
					disabled={isCreatingProject}
					onClick={() => void handleCreateProject()}
				>
					{isCreatingProject ? "Creating…" : "New Project"}
				</Button>
			</section>

			{/* Pending Invitations */}
			{invitations.length > 0 && (
				<section className="flex flex-col gap-4">
					<div className="flex items-center justify-between">
						<h3 className="text-lg font-medium">Pending Invitations</h3>
						<Badge variant="secondary">{invitations.length}</Badge>
					</div>

					<div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
						{invitations.map((invitation) => (
							<Card
								key={invitation.id}
								className="border-primary/30 bg-primary/5"
							>
								<CardHeader>
									<CardTitle className="text-base">
										{invitation.project?.name ?? "Unknown Project"}
									</CardTitle>
									<CardDescription>
										Invited by{" "}
										{invitation.invited_by_user?.name ??
											invitation.invited_by_user?.email ??
											"Unknown"}
									</CardDescription>
								</CardHeader>
								<CardContent className="text-sm text-muted-foreground">
									<p>
										Role:{" "}
										<Badge variant="outline" className="ml-1 capitalize">
											{invitation.role}
										</Badge>
									</p>
								</CardContent>
								<CardFooter className="gap-2">
									<Button
										size="sm"
										onClick={() => handleAcceptInvitation(invitation.id)}
									>
										<Check className="mr-1 h-4 w-4" />
										Accept
									</Button>
									<Button
										size="sm"
										variant="outline"
										onClick={() => handleDeclineInvitation(invitation.id)}
									>
										<X className="mr-1 h-4 w-4" />
										Decline
									</Button>
								</CardFooter>
							</Card>
						))}
					</div>
				</section>
			)}

			{/* My Projects */}
			<section className="flex flex-col gap-4">
				<div className="flex items-center justify-between">
					<h3 className="text-lg font-medium">My Projects</h3>
				</div>

				{isLoading ? (
					<div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
						{["alpha", "bravo", "charlie"].map((skeletonKey) => (
							<Card
								key={`project-skeleton-${skeletonKey}`}
								className="border-dashed animate-pulse"
							>
								<CardHeader>
									<CardTitle className="h-6 w-32 rounded bg-muted" />
									<CardDescription className="h-4 w-48 rounded bg-muted" />
								</CardHeader>
								<CardContent className="space-y-3">
									<div className="h-4 w-40 rounded bg-muted" />
									<div className="h-4 w-24 rounded bg-muted" />
								</CardContent>
								<CardFooter>
									<div className="h-9 w-24 rounded bg-muted" />
								</CardFooter>
							</Card>
						))}
					</div>
				) : membershipsStatus.type === "error" ? (
					<Card className="border-destructive/50">
						<CardHeader>
							<CardTitle>Projects unavailable</CardTitle>
							<CardDescription>
								We couldn&apos;t load your projects just now. Please retry.
							</CardDescription>
						</CardHeader>
						<CardFooter>
							<Button
								variant="outline"
								onClick={() => membershipsStatus.retry()}
							>
								Try again
							</Button>
						</CardFooter>
					</Card>
				) : ownedProjects.length === 0 ? (
					<Card className="border-dashed">
						<CardHeader>
							<CardTitle>No projects yet</CardTitle>
							<CardDescription>
								Create your first project to start building node-driven
								geometry.
							</CardDescription>
						</CardHeader>
						<CardFooter>
							<Button
								disabled={isCreatingProject}
								onClick={() => void handleCreateProject()}
							>
								{isCreatingProject ? "Creating…" : "Create project"}
							</Button>
						</CardFooter>
					</Card>
				) : (
					<div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
						{ownedProjects.map((membership) => {
							const project = membership.project
							if (!project) return null

							const createdAt =
								project.created_at &&
								new Date(project.created_at).toLocaleString()
							const updatedAt =
								project.updated_at &&
								new Date(project.updated_at).toLocaleString()

							return (
								<Card
									key={project.id}
									role="button"
									tabIndex={0}
									onClick={() => navigateToProject(project.id, project.name)}
									onKeyDown={(event) => {
										if (event.key === "Enter" || event.key === " ") {
											event.preventDefault()
											navigateToProject(project.id, project.name)
										}
									}}
									className="transition hover:border-primary/60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring cursor-pointer"
								>
									<CardHeader>
										<CardTitle>{project.name}</CardTitle>
										<CardDescription>
											{project.description || "Node-based workflow"}
										</CardDescription>
									</CardHeader>
									<CardContent className="text-sm text-muted-foreground space-y-1">
										{createdAt && <p>Created {createdAt}</p>}
										{updatedAt && <p>Updated {updatedAt}</p>}
									</CardContent>
									<CardFooter className="justify-between">
										<Button
											variant="ghost"
											className="text-destructive hover:text-destructive"
											disabled={deletingProjectId === project.id}
											onClick={(event) =>
												void handleDeleteProject(
													event,
													project.id,
													project.name,
												)
											}
											onKeyDown={(event) => event.stopPropagation()}
										>
											<Trash2 />
											{deletingProjectId === project.id
												? "Deleting…"
												: "Delete"}
										</Button>
										<Button variant="ghost">Open</Button>
									</CardFooter>
								</Card>
							)
						})}
					</div>
				)}
			</section>

			{/* Shared With Me */}
			{sharedProjects.length > 0 && (
				<section className="flex flex-col gap-4">
					<div className="flex items-center justify-between">
						<h3 className="text-lg font-medium">Shared With Me</h3>
					</div>

					<div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
						{sharedProjects.map((membership) => {
							const project = membership.project
							if (!project) return null

							const updatedAt =
								project.updated_at &&
								new Date(project.updated_at).toLocaleString()

							return (
								<Card
									key={project.id}
									role="button"
									tabIndex={0}
									onClick={() => navigateToProject(project.id, project.name)}
									onKeyDown={(event) => {
										if (event.key === "Enter" || event.key === " ") {
											event.preventDefault()
											navigateToProject(project.id, project.name)
										}
									}}
									className="transition hover:border-primary/60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring cursor-pointer"
								>
									<CardHeader>
										<div className="flex items-center justify-between">
											<CardTitle>{project.name}</CardTitle>
											<Badge variant="outline" className="capitalize">
												{membership.role}
											</Badge>
										</div>
										<CardDescription>
											{project.description || "Node-based workflow"}
										</CardDescription>
									</CardHeader>
									<CardContent className="text-sm text-muted-foreground space-y-1">
										{updatedAt && <p>Updated {updatedAt}</p>}
									</CardContent>
									<CardFooter className="justify-end">
										<Button variant="ghost">Open</Button>
									</CardFooter>
								</Card>
							)
						})}
					</div>
				</section>
			)}
		</div>
	)
}
